import { DelayCategorySelector } from "@/controls/selectors/DelayCategorySelector";
import { DelayCodePicker } from "@/controls/selectors/DelayCodePicker";
import { justifyDelay } from "@/pages/delays/delays-manager/repositories/DelaysManagerRepository";
import { ErrorModal, MesfModal, getMomentTz } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTimerContext } from "../../../context/timer.provider";
import { useDelayNotificationAccess } from "../../../hooks/useDelayNotificationAccess";
import { UnjustifiedDelay } from "../../../models/eaf.models";

interface JustifyDelayFormData {
	DelayCodeId: number | null;
	DelayCategoryId: number | null;
	Comments: string | null;
}

const useJustifyDelayStyles = makeStyles((theme) => ({
	consistentFieldWrapper: {
		marginTop: theme.spacing(0.5),
		"& .MuiInputBase-root": {
			minHeight: "57px",
			height: "57px",
			alignItems: "center",
		},

		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 19px) scale(1)",
			"&.MuiInputLabel-shrink": {
				transform: "translate(14px, -6px) scale(0.75)",
			},
		},
		"& .MuiIconButton-root": {
			padding: theme.spacing(1.5),
		},
	},
	commentsInput: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
}));

interface JustifyDelayProps {
	open: boolean;
	onHide: (justifiedDelay?: boolean) => void;
	assetId: number | null;
	refreshData: () => Promise<void>;
	delay: UnjustifiedDelay | null;
}

const momentTz = getMomentTz();

const formatDuration = (totalSeconds: number | null | undefined): string => {
	if (totalSeconds === null || totalSeconds === undefined || totalSeconds < 0) {
		return "00:00:00";
	}
	const duration = moment.duration(totalSeconds, "seconds");
	const hours = Math.floor(duration.asHours());
	const minutes = duration.minutes();
	const seconds = duration.seconds();
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
		2,
		"0",
	)}:${String(seconds).padStart(2, "0")}`;
};

export const JustifyDelay = ({
	open,
	onHide,
	assetId,
	refreshData,
	delay,
}: JustifyDelayProps) => {
	const classes = useJustifyDelayStyles();
	const { pause } = useTimerContext();
	const { dismissDelayJustification } = useDelayNotificationAccess();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<JustifyDelayFormData>({
		defaultValues: {
			DelayCodeId: null,
			DelayCategoryId: null,
			Comments: null,
		},
	});

	const commentsWatch = watch("Comments");

	useEffect(() => {
		if (open) {
			pause();
			reset({
				DelayCodeId: delay?.DelayCodeId ?? null,
				DelayCategoryId: delay?.DelayCategoryId ?? null,
				Comments: delay?.Comments ?? null,
			});
			setError(null);
		}
	}, [open, delay, reset]);

	const handleRemindLater = () => {
		dismissDelayJustification();
		onHide();
	};

	const onSubmit: SubmitHandler<JustifyDelayFormData> = async (data) => {
		if (!delay?.DelayId) {
			setError("Delay not found.");
			return;
		}
		setIsSubmitLoading(true);
		setError(null);
		try {
			const response = await justifyDelay(
				delay?.DelayId,
				data.DelayCodeId,
				data.DelayCategoryId,
				data.Comments,
				null,
				null,
				null,
			);
			if (response.ok) {
				await refreshData();
				onHide(true);
			} else {
				setError(response.message || "Failed to justify delay.");
			}
		} catch (err: any) {
			setError(err.message || "An unexpected error occurred.");
		} finally {
			setIsSubmitLoading(false);
		}
	};

	const formattedStart = useMemo(() => {
		return delay?.Start
			? momentTz(delay.Start).format("MM/DD/YYYY HH:mm:ss")
			: "N/A";
	}, [delay?.Start]);

	const formattedEnd = useMemo(() => {
		return delay?.End
			? momentTz(delay.End).format("MM/DD/YYYY HH:mm:ss")
			: "N/A";
	}, [delay?.End]);

	const formattedDurationValue = useMemo(() => {
		return formatDuration(delay?.Duration);
	}, [delay?.Duration]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={() => {
					handleRemindLater();
					onHide();
				}}
				title="Justify Delay"
				maxWidth="md"
				id="justify-delay-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						{!delay || !assetId ? (
							<Grid item xs={12}>
								<Typography color="error">
									Missing Delay Information. Cannot justify.
								</Typography>
							</Grid>
						) : (
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Typography>Please justify the delay to continue.</Typography>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										label="Duration (hh:mm:ss)"
										value={formattedDurationValue}
										variant="outlined"
										size="small"
										fullWidth
										disabled
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										label="Delay Start"
										value={formattedStart}
										variant="outlined"
										size="small"
										fullWidth
										disabled
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										label="Delay End"
										value={formattedEnd}
										variant="outlined"
										size="small"
										fullWidth
										disabled
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										label="Asset Name"
										value={delay.AssetName ?? "N/A"}
										variant="outlined"
										size="medium"
										fullWidth
										disabled
										style={{ marginTop: "13px" }}
										InputProps={{
											style: {
												height: "53px",
											},
										}}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={4}
									className={classes.consistentFieldWrapper}
								>
									<Controller
										name="DelayCodeId"
										control={control}
										render={({ field }) => (
											<DelayCodePicker
												key={`DelayCodeId-${assetId}`}
												assetId={assetId}
												value={field.value}
												onChange={(val, delCode) => {
													field.onChange(val);
													if (delCode && delCode.DelayCategoryId) {
														setValue(
															"DelayCategoryId",
															delCode.DelayCategoryId,
															{ shouldValidate: true },
														);
													}
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12} sm={4} style={{ marginTop: "8px" }}>
									<Controller
										name="DelayCategoryId"
										control={control}
										render={({ field }) => (
											<DelayCategorySelector
												value={field.value ?? null}
												onChange={field.onChange}
												assetId={assetId}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="Comments"
										control={control}
										render={({ field }) => (
											<TextField
												label="Comments"
												className={classes.commentsInput}
												variant="outlined"
												maxRows={4}
												minRows={4}
												fullWidth
												margin="dense"
												multiline
												autoComplete="off"
												value={field.value ?? ""}
												onChange={(e) => {
													if (e.target.value.length <= 200) field.onChange(e);
												}}
												InputProps={{
													style: {
														height: "90px",
														padding: "0px",
														fontSize: "12px",
													},
												}}
											/>
										)}
									/>
									<Grid container justifyContent="flex-end">
										<Grid item>
											<span>{`${commentsWatch?.length ?? 0}/200`}</span>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid container justifyContent="flex-end" spacing={2}>
							<Grid item xs={12} sm={3}>
								<Button
									variant="contained"
									color="secondary"
									onClick={handleRemindLater}
									disabled={isSubmitLoading}
									size="large"
									fullWidth
								>
									Remind Me Later {}
								</Button>
							</Grid>
							<Grid item xs={12} sm={3}>
								<Button
									variant="contained"
									color="primary"
									type="submit"
									size="large"
									fullWidth
									disabled={isSubmitLoading || watch("DelayCodeId") === null}
									startIcon={
										isSubmitLoading ? <CircularProgress size={20} /> : null
									}
								>
									Justify
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError(null)} />
		</>
	);
};
